import React, { useRef, useEffect, useCallback, useState } from 'react';
import { autoResize } from '../utils/autoResize';
import { RichTextParagraph } from './RichTextParagraph';
import { useContentEditable } from '../hooks/useContentEditable';
import { linkUtils } from '../utils/linkUtils';
import { ImageToolbar } from './ImageToolbar';
import { CropModal } from './CropModal';
import { ImageSearchModal } from './ImageSearchModal';
import { useImageSearch } from '../hooks/useImageSearch';

// EditableCaption component with proper cursor handling
interface EditableCaptionProps {
  caption: string;
  onChange: (caption: string) => void;
}

const EditableCaption: React.FC<EditableCaptionProps> = ({ caption, onChange }) => {
  const { ref, onInput, onKeyDown } = useContentEditable({
    value: caption,
    onChange,
  });

  return (
    <figcaption 
      ref={ref}
      contentEditable={true}
      suppressContentEditableWarning={true}
      onInput={onInput}
      onKeyDown={onKeyDown}
      onFocus={(e) => {
        e.currentTarget.style.borderColor = '#007cba';
      }}
      onBlur={(e) => {
        e.currentTarget.style.borderColor = 'transparent';
      }}
      style={{ 
        fontSize: '14px', 
        color: '#666', 
        marginTop: '8px',
        fontStyle: 'italic',
        outline: 'none',
        border: '2px solid transparent',
        borderRadius: '4px',
        padding: '4px 8px',
        minHeight: '1.2em',
        transition: 'border-color 0.2s ease'
      }}
    >
      {!caption && (
        <span 
          data-rich-text-placeholder="Add caption..."
          style={{
            pointerEvents: 'none',
            userSelect: 'none',
            WebkitUserSelect: 'none',
            MozUserSelect: 'none',
            msUserSelect: 'none'
          }}
        />
      )}
      {caption}
    </figcaption>
  );
};

interface BlockEditProps {
  attributes: Record<string, any>;
  setAttributes: (attributes: Record<string, any>) => void;
  blockName: string;
  clientId: string;
  onImageClick?: (x: number, y: number) => void;
  onDeleteBlock?: (clientId: string) => void;
}

export const BlockEdit: React.FC<BlockEditProps> = ({ 
  attributes, 
  setAttributes, 
  blockName, 
  clientId,
  onImageClick,
  onDeleteBlock
}) => {
  const { content, level, url, alt, caption, values, value } = attributes;
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  
  // Auto-resize function for textarea blocks
  const handleAutoResize = (textarea: HTMLTextAreaElement) => {
    autoResize(textarea, blockName);
  };

  // Handle backspace deletion when cursor is at the beginning
  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement | HTMLTextAreaElement>) => {
    if (e.key === 'Backspace') {
      let cursorPosition = 0;
      
      if (e.currentTarget instanceof HTMLTextAreaElement) {
        cursorPosition = e.currentTarget.selectionStart || 0;
      } else {
        const selection = window.getSelection();
        cursorPosition = selection?.anchorOffset || 0;
      }
      
      // Check if cursor is at the beginning and content is empty or only whitespace
      if (cursorPosition === 0 && (!content || content.trim() === '')) {
        e.preventDefault();
        if (onDeleteBlock) {
          onDeleteBlock(clientId);
        }
      }
    }
  };
  
  // Set initial height on mount and when content changes (for textarea blocks only)
  useEffect(() => {
    if (textareaRef.current && blockName !== 'core/paragraph') {
      setTimeout(() => {
        handleAutoResize(textareaRef.current!);
      }, 0);
    }
  }, [content, blockName]);
  
  switch (blockName) {
    case 'core/paragraph':
      return (
        <div className="block-container">
          <RichTextParagraph
            content={content || ''}
            clientId={clientId}
            onChange={(newContent) => setAttributes({ content: newContent })}
            onDelete={() => {
              if (onDeleteBlock) {
                onDeleteBlock(clientId);
              }
            }}
            placeholder="Enter paragraph text..."
          />
        </div>
      );
      
    case 'core/heading':
      const headingLevel = level || 2; // Default to H2 if no level specified
      const headingRef = useRef<HTMLDivElement>(null);

      const handleHeadingInput = useCallback((e: React.FormEvent<HTMLDivElement>) => {
        const element = e.currentTarget;
        const textContent = element.textContent || '';
        
        // Convert URLs to links automatically
        const htmlWithLinks = linkUtils.textToHtmlWithLinks(textContent);
        
        // Only update if content has changed
        if (htmlWithLinks !== element.innerHTML) {
          element.innerHTML = htmlWithLinks;
          
          // Restore cursor position after HTML update
          const selection = window.getSelection();
          if (selection && selection.rangeCount > 0) {
            const range = selection.getRangeAt(0);
            const cursorOffset = range.startOffset;
            
            // Move cursor to end of the content
            const textLength = textContent.length;
            if (cursorOffset >= textLength) {
              const newRange = document.createRange();
              newRange.selectNodeContents(element);
              newRange.collapse(false);
              selection.removeAllRanges();
              selection.addRange(newRange);
            }
          }
        }
        
        setAttributes({ content: textContent });
      }, [setAttributes]);

      const handleHeadingKeyDown = useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
        if (e.key === 'Backspace' && e.currentTarget.textContent === '') {
          e.preventDefault();
          if (onDeleteBlock) {
            onDeleteBlock(clientId);
          }
        }
      }, [onDeleteBlock, clientId]);

      // Set initial content only once
      useEffect(() => {
        if (headingRef.current && !headingRef.current.textContent) {
          const htmlWithLinks = linkUtils.textToHtmlWithLinks(content || '');
          headingRef.current.innerHTML = htmlWithLinks;
        }
      }, []);

      return (
        <div className="block-container">
          <div
            ref={headingRef}
            className={`block-editor-rich-text__editable block-editor-block-list__block wp-block is-selected wp-block-heading rich-text heading-input heading-input--h${headingLevel}`}
            id={`block-${clientId}`}
            aria-label="Block: Heading"
            data-block={clientId}
            data-type="core/heading"
            data-title="Heading"
            contentEditable={true}
            data-wp-block-attribute-key="content"
            style={{
              whiteSpace: 'pre-wrap',
              minWidth: '1px',
              overflowWrap: 'break-word',
              lineBreak: 'after-white-space' as any,
              margin: '0',
              fontSize: headingLevel === 1 ? '32px' : headingLevel === 2 ? '28px' : headingLevel === 3 ? '24px' : headingLevel === 4 ? '20px' : headingLevel === 5 ? '18px' : '16px',
              fontWeight: 'bold',
              lineHeight: '1.2',
              outline: 'none',
              WebkitNbspMode: 'space' as any,
              WebkitUserModify: 'read-write' as any
            } as React.CSSProperties}
            onInput={handleHeadingInput}
            onKeyDown={handleHeadingKeyDown}
            suppressContentEditableWarning={true}
            data-placeholder="Heading"
          />
        </div>
      );
      
    case 'core/image':
      return <ImageBlock 
        url={url} 
        alt={alt} 
        caption={caption} 
        setAttributes={setAttributes}
        onImageClick={onImageClick}
        onDeleteBlock={onDeleteBlock}
        clientId={clientId}
      />;
      
    case 'core/list':
      return (
        <div style={{ margin: '0em 0', padding: '0', border: 'none', backgroundColor: 'transparent' }}>
          <textarea
            ref={textareaRef}
            value={values || ''}
            onChange={(e) => {
              setAttributes({ values: e.target.value });
              handleAutoResize(e.target);
            }}
            onKeyDown={handleKeyDown}
            style={{ 
              width: '100%', 
              minHeight: '32px',
              height: 'auto',
              border: 'none', 
              borderRadius: '0', 
              padding: '0',
              fontFamily: 'inherit',
              fontSize: '16px',
              lineHeight: '1.5',
              resize: 'none',
              overflow: 'hidden',
              backgroundColor: 'transparent',
              whiteSpace: 'pre-wrap',
              minWidth: '1px',
              outline: 'none',
              direction: 'ltr',
              unicodeBidi: 'normal'
            }}
            placeholder="Enter list items (one per line)..."
          />
        </div>
      );
      
    case 'core/quote':
      return (
        <div style={{ margin: '0em 0', padding: '0', border: 'none', backgroundColor: 'transparent' }}>
          <textarea
            ref={textareaRef}
            value={value || ''}
            onChange={(e) => {
              setAttributes({ value: e.target.value });
              handleAutoResize(e.target);
            }}
            onKeyDown={handleKeyDown}
            style={{ 
              width: '100%', 
              minHeight: '32px',
              height: 'auto',
              border: 'none', 
              borderRadius: '0', 
              padding: '0',
              fontFamily: 'inherit',
              fontSize: '16px',
              lineHeight: '1.5',
              resize: 'none',
              overflow: 'hidden',
              backgroundColor: 'transparent',
              whiteSpace: 'pre-wrap',
              minWidth: '1px',
              outline: 'none',
              fontStyle: 'italic'
            }}
            placeholder="Enter quote text..."
          />
        </div>
      );
      
    default:
      return (
        <div style={{ margin: '0em 0', padding: '0', border: 'none', backgroundColor: 'transparent' }}>
          <textarea
            ref={textareaRef}
            value={content || ''}
            onChange={(e) => {
              setAttributes({ content: e.target.value });
              handleAutoResize(e.target);
            }}
            onKeyDown={handleKeyDown}
            style={{ 
              width: '100%', 
              minHeight: '32px',
              height: 'auto',
              border: 'none', 
              borderRadius: '0', 
              padding: '0',
              fontFamily: 'inherit',
              fontSize: '16px',
              lineHeight: '1.5',
              resize: 'none',
              overflow: 'hidden',
              backgroundColor: 'transparent',
              whiteSpace: 'pre-wrap',
              minWidth: '1px',
              outline: 'none',
              direction: 'ltr',
              unicodeBidi: 'normal'
            }}
            placeholder="Enter text..."
          />
        </div>
      );
  }
};

// ImageBlock component with toolbar and crop functionality
interface ImageBlockProps {
  url?: string;
  alt?: string;
  caption?: string;
  setAttributes: (attributes: Record<string, any>) => void;
  onImageClick?: (x: number, y: number) => void;
  onDeleteBlock?: (clientId: string) => void;
  clientId: string;
}

const ImageBlock: React.FC<ImageBlockProps> = ({
  url,
  alt,
  caption,
  setAttributes,
  onImageClick,
  onDeleteBlock,
  clientId
}) => {
  const [showToolbar, setShowToolbar] = useState(false);
  const [toolbarPosition, setToolbarPosition] = useState({ blockId: '', x: 0, y: 0, popupX: 0, popupY: 0 });
  const [showCropModal, setShowCropModal] = useState(false);
  const [showImageSearch, setShowImageSearch] = useState(false);
  const [imageAlignment, setImageAlignment] = useState<'left' | 'center' | 'right'>('center');
  
  // Use image search hook
  const {
    searchImages,
    searchLoading,
    selectedSources,
    hasMoreImages,
    handleImageSearch,
    handleSourceToggle,
    closeImageSearch
  } = useImageSearch();

  const handleImageClick = (e: React.MouseEvent<HTMLImageElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const clickX = rect.left + (rect.width / 2);
    const clickY = rect.top;
    
    setToolbarPosition({
      blockId: clientId,
      x: clickX,
      y: clickY,
      popupX: clickX,
      popupY: clickY - 60
    });
    setShowToolbar(true);
    
    if (onImageClick) {
      onImageClick(clickX, clickY);
    }
  };

  const handleReplaceImage = () => {
    setShowImageSearch(true);
    setShowToolbar(false);
  };

  const handleCropImage = () => {
    setShowCropModal(true);
    setShowToolbar(false);
  };

  const handleRemoveImage = () => {
    if (onDeleteBlock) {
      onDeleteBlock(clientId);
    }
  };

  const handleAlignImage = (alignment: 'left' | 'center' | 'right') => {
    setImageAlignment(alignment);
    setShowToolbar(false);
  };

  const handleImageSelect = (image: any) => {
    setAttributes({
      url: image.url,
      alt: image.alt || image.caption,
      caption: image.attribution || image.caption
    });
    setShowImageSearch(false);
  };

  const handleCropConfirm = (croppedImageUrl: string) => {
    setAttributes({ url: croppedImageUrl });
    setShowCropModal(false);
  };

  const handleLoadMoreImages = () => {
    handleImageSearch('', true);
  };

  const getImageStyle = () => {
    const baseStyle = {
      maxWidth: '100%',
      height: 'auto',
      display: 'block',
      cursor: 'pointer'
    };

    switch (imageAlignment) {
      case 'left':
        return { ...baseStyle, margin: '0 auto 0 0' };
      case 'right':
        return { ...baseStyle, margin: '0 0 0 auto' };
      case 'center':
      default:
        return { ...baseStyle, margin: '0 auto' };
    }
  };

  return (
    <>
      <div style={{ margin: '0em 0', padding: '0', border: 'none', backgroundColor: 'transparent', position: 'relative' }}>
        {url ? (
          <figure style={{ margin: '0', textAlign: imageAlignment }}>
            <img 
              src={url} 
              alt={alt || ''} 
              onClick={handleImageClick}
              style={getImageStyle()} 
            />
            <EditableCaption
              caption={caption || ''}
              onChange={(newCaption) => setAttributes({ caption: newCaption })}
            />
          </figure>
        ) : (
          <div 
            style={{ 
              border: '2px dashed #ccc', 
              padding: '20px', 
              textAlign: 'center',
              backgroundColor: '#f9f9f9',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
            onClick={() => setShowImageSearch(true)}
          >
            <p style={{ margin: '0', color: '#666' }}>Click to add an image</p>
          </div>
        )}
      </div>

      {/* Image Toolbar */}
      <ImageToolbar
        position={toolbarPosition}
        isVisible={showToolbar}
        onReplace={handleReplaceImage}
        onRemove={handleRemoveImage}
        onCrop={handleCropImage}
        onAlign={handleAlignImage}
      />

      {/* Crop Modal */}
      <CropModal
        isOpen={showCropModal}
        imageSrc={url || ''}
        onCancel={() => setShowCropModal(false)}
        onConfirm={handleCropConfirm}
        loading={false}
      />

      {/* Image Search Modal */}
      <ImageSearchModal
        isOpen={showImageSearch}
        selectedSources={selectedSources}
        onSourceToggle={handleSourceToggle}
        onSearch={handleImageSearch}
        onSelect={handleImageSelect}
        onClose={() => setShowImageSearch(false)}
        images={searchImages}
        loading={searchLoading}
        hasMore={hasMoreImages}
        loadMore={handleLoadMoreImages}
      />
    </>
  );
};
